import { pick } from './utils';
import PropTypes from 'prop-types';
import isEqual from 'fast-deep-equal';
import React, { useEffect, useRef } from 'react';
import { bind, clear } from 'size-sensor';

const EchartsReactCore = (props) => {
  const {
    echarts,
    option,
    notMerge,
    lazyUpdate,
    style,
    className,
    theme,
    onChartReady,
    showLoading,
    loadingOption,
    onEvents,
    opts,
    shouldSetOption,
  } = props;

  const echartsElement = useRef(null); // echarts div element
  const prevPropsRef = useRef({}); // to store previous props

  // first add
  useEffect(() => {
    rerender();
  }, []); // componentDidMount

  // update
  useEffect(() => {
    const prevProps = prevPropsRef.current;

    // 以下属性修改的时候，需要 dispose 之后再新建
    // 1. 切换 theme 的时候
    // 2. 修改 opts 的时候
    // 3. 修改 onEvents 的时候，这样可以取消所以之前绑定的事件 issue #151
    if (
      !isEqual(prevProps.theme, theme) ||
      !isEqual(prevProps.opts, opts) ||
      !isEqual(prevProps.onEvents, onEvents)
    ) {
      dispose();
      rerender(); // 重建
      return;
    }

    // 当这些属性保持不变的时候，不 setOption
    const pickKeys = ['option', 'notMerge', 'lazyUpdate', 'showLoading', 'loadingOption'];
    if (isEqual(pick(props, pickKeys), pick(prevProps, pickKeys))) {
      return;
    }

    // 判断是否需要 setOption，由开发者自己来确定。默认为 true
    if (
      typeof shouldSetOption === 'function' &&
      !shouldSetOption(prevProps, props)
    ) {
      return;
    }

    const echartObj = renderEchartDom();
    // 样式修改的时候，可能会导致大小变化，所以触发一下 resize
    if (
      !isEqual(prevProps.style, style) ||
      !isEqual(prevProps.className, className)
    ) {
      try {
        echartObj.resize();
      } catch (e) {
        console.warn(e);
      }
    }

    // Update the previous props reference
    prevPropsRef.current = props;
  }, [theme, opts, onEvents, option, notMerge, lazyUpdate, style, className]); // componentDidUpdate

  // remove
  useEffect(() => {
    return () => {
      dispose();
    };
  }, []); // componentWillUnmount

  // return the echart object
  const getEchartsInstance = () =>
    echarts.getInstanceByDom(echartsElement.current) ||
    echarts.init(echartsElement.current, theme, opts);

  // dispose echarts and clear size-sensor
  const dispose = () => {
    if (echartsElement.current) {
      try {
        clear(echartsElement.current);
      } catch (e) {
        console.warn(e);
      }
      // dispose echarts instance
      echarts.dispose(echartsElement.current);
    }
  };

  const rerender = () => {
    const echartObj = renderEchartDom();
    bindEvents(echartObj, onEvents || {});

    // on chart ready
    if (typeof onChartReady === 'function') onChartReady(echartObj);
    // on resize
    if (echartsElement.current) {
      bind(echartsElement.current, () => {
        try {
          echartObj.resize();
        } catch (e) {
          console.warn(e);
        }
      });
    }
  };

  // bind the events
  const bindEvents = (instance, events) => {
    const _bindEvent = (eventName, func) => {
      // ignore the event config which not satisfy
      if (typeof eventName === 'string' && typeof func === 'function') {
        // binding event
        // instance.off(eventName); // 已经 dispose 在重建，所以无需 off 操作
        instance.on(eventName, (param) => {
          func(param, instance);
        });
      }
    };

    // loop and bind
    for (const eventName in events) {
      if (Object.prototype.hasOwnProperty.call(events, eventName)) {
        _bindEvent(eventName, events[eventName]);
      }
    }
  };

  // render the dom
  const renderEchartDom = () => {
    // init the echart object
    const echartObj = getEchartsInstance();
    // set the echart option
    echartObj.setOption(
      option,
      notMerge || false,
      lazyUpdate || false
    );
    // set loading mask
    if (showLoading) echartObj.showLoading(loadingOption || null);
    else echartObj.hideLoading();

    return echartObj;
  };

  const newStyle = {
    height: 300,
    ...style
  };

  // for render
  return (
    <div
      ref={echartsElement}
      style={newStyle}
      className={`echarts-for-react ${className}`}
    />
  );
};

EchartsReactCore.propTypes = {
  option: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  echarts: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  notMerge: PropTypes.bool,
  lazyUpdate: PropTypes.bool,
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  className: PropTypes.string,
  theme: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChartReady: PropTypes.func,
  showLoading: PropTypes.bool,
  loadingOption: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onEvents: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  opts: PropTypes.shape({
    devicePixelRatio: PropTypes.number,
    renderer: PropTypes.oneOf(['canvas', 'svg']),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null, undefined, 'auto'])]),
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null, undefined, 'auto'])])
  }),
  shouldSetOption: PropTypes.func
};

EchartsReactCore.defaultProps = {
  echarts: {},
  notMerge: false,
  lazyUpdate: false,
  style: {},
  className: '',
  theme: null,
  onChartReady: () => { },
  showLoading: false,
  loadingOption: null,
  onEvents: {},
  opts: {},
  shouldSetOption: () => true
};

export default EchartsReactCore;